import { useCallback, useEffect, useState, type FormEvent } from "react";
import { get, post, type Deliverable, type Me, type Revision, type Trip } from "../lib/api.ts";
import { RevisionReview } from "./deliverables/RevisionReview.tsx";
import { formatBytes } from "../lib/transfer.ts";
import { uploadRevision, type UploadProgress } from "../lib/upload.ts";

const STATUS: Record<string, string> = {
  PLANNED: "Planned",
  EDITING: "Editing",
  IN_REVIEW: "In review",
  CHANGES_REQUESTED: "Changes requested",
  APPROVED: "Approved",
  DELIVERED: "Delivered",
};
const PHASE: Record<UploadProgress["phase"], string> = {
  hashing: "Reading your file",
  preparing: "Preparing",
  uploading: "Uploading",
  completing: "Finishing upload",
  verifying: "Checking the upload",
  publishing: "Publishing",
  done: "Done",
};

export function ProductionTab({ trip, me }: { trip: Trip; me: Me }) {
  const [items, setItems] = useState<Deliverable[] | null>(null);
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const canCreate = trip.roles.some((r) => ["ADMIN", "EDITOR"].includes(r));

  const load = useCallback(async () => {
    setItems((await get<{ items: Deliverable[] }>(`/v1/trips/${trip.id}/deliverables`)).items);
  }, [trip.id]);
  useEffect(() => {
    load().catch((e: Error) => setError(e.message));
  }, [load]);

  async function create(e: FormEvent) {
    e.preventDefault();
    try {
      const d = await post<Deliverable>(`/v1/trips/${trip.id}/deliverables`, { title });
      setTitle("");
      await load();
      setOpen(d.id);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <section className="stack" aria-label="Edits and review">
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {items === null ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No edits yet.</strong>
          </p>
          <p className="muted">
            Editors can start a deliverable and upload each cut as a new version while the trip is
            still going.
          </p>
        </div>
      ) : (
        <ul className="list">
          {items.map((d) => (
            <li key={d.id} className="item column">
              <button
                className="linklike row"
                onClick={() => setOpen(open === d.id ? null : d.id)}
                aria-expanded={open === d.id}
              >
                <strong className="grow">{d.title}</strong>
                <span
                  className={`pill ${d.status === "APPROVED" || d.status === "DELIVERED" ? "good" : "wait"}`}
                >
                  {STATUS[d.status] ?? d.status}
                </span>
              </button>
              {open === d.id && (
                <DeliverablePanel
                  trip={trip}
                  me={me}
                  // The task's author may approve whatever their role, so the button has to know
                  // who that is. It comes from GET /deliverables (`approver`), which the server
                  // fills only for a task the reader can see; null means a project with no task
                  // in view, and then the reviewer-or-admin rule applies.
                  taskOwnerId={d.approver?.userId ?? null}
                  deliverable={d}
                  onChanged={load}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {canCreate && (
        <form onSubmit={create} className="row">
          <label className="grow">
            <span className="sr">Deliverable title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="New deliverable, e.g. “Day 3 recap”"
              maxLength={200}
              required
            />
          </label>
          <button className="button primary">Add deliverable</button>
        </form>
      )}
    </section>
  );
}

function DeliverablePanel({
  trip,
  me,
  taskOwnerId,
  deliverable,
  onChanged,
}: {
  trip: Trip;
  me: Me;
  /** The task's owner when the project comes from a task; they and the admin approve. */
  taskOwnerId: string | null;
  deliverable: Deliverable;
  onChanged: () => Promise<void>;
}) {
  const [revs, setRevs] = useState<Revision[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const isEditor = trip.roles.includes("EDITOR");
  const base = `/v1/trips/${trip.id}`;

  const load = useCallback(async () => {
    const r = await get<{ revisions: Revision[] }>(
      `${base}/deliverables/${deliverable.id}/revisions`,
    );
    setRevs(r.revisions);
    setSelected((s) => s ?? r.revisions[r.revisions.length - 1]?.id ?? null);
  }, [base, deliverable.id]);
  useEffect(() => {
    load().catch((e: Error) => setError(e.message));
  }, [load]);

  async function upload(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setError(null);
    try {
      const nodes = await get<{ items: { id: string }[] }>(`${base}/storage-nodes`);
      const node = nodes.items[0];
      if (!node)
        throw new Error(
          "This trip has no storage destination. New trips get one automatically; ask the platform owner to repair this one.",
        );
      await uploadRevision({
        tripId: trip.id,
        deliverableId: deliverable.id,
        nodeId: node.id,
        file,
        notes,
        onProgress: setProgress,
      });
      setFile(null);
      setNotes("");
      setSelected(null);
      await load();
      await onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setProgress(null);
    }
  }

  const sel = revs?.find((r) => r.id === selected) ?? null;
  const uploading = progress !== null;

  return (
    <div className="stack panelbody">
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {revs === null ? (
        <p className="muted">Loading…</p>
      ) : revs.length === 0 ? (
        <p className="muted">No versions uploaded yet.</p>
      ) : (
        <div className="version-cards" role="tablist" aria-label="Versions">
          {revs.map((r) => {
            const last = r.decisions.at(-1);
            return (
              <button
                key={r.id}
                role="tab"
                aria-selected={r.id === selected}
                className="version-card"
                onClick={() => setSelected(r.id)}
              >
                <strong>V{r.versionNumber}</strong>
                <span className="small muted">{new Date(r.createdAt).toLocaleDateString()}</span>
                {last ? (
                  <span className={`pill ${last.decision === "APPROVE" ? "good" : "wait"}`}>
                    {last.decision === "APPROVE" ? "Approved" : "Changes requested"}
                  </span>
                ) : (
                  <span className="pill">{r.isCurrent ? "Current" : "Superseded"}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
      {sel && (
        <RevisionReview
          key={sel.id}
          trip={trip}
          me={me}
          revision={sel}
          taskOwnerId={taskOwnerId}
          onChanged={async () => {
            await load();
            await onChanged();
          }}
        />
      )}

      {isEditor && (
        <form onSubmit={upload} className="stack panel">
          <h3>Upload a new version</h3>
          <p className="muted">
            Edit in your own editing software, then upload the exported video here. This uses your
            current internet connection. If interrupted, reselect the same file to resume with its
            original notes.
          </p>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={uploading}
            aria-label="Exported video file"
          />
          {file && (
            <p className="muted">
              {file.name} · {formatBytes(file.size)}
            </p>
          )}
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What changed? (optional)"
            maxLength={4000}
            disabled={uploading}
          />
          <button className="button primary" disabled={!file || uploading}>
            Upload version
          </button>
          {progress && (
            <div role="status">
              <progress
                max={1}
                value={progress.phase === "verifying" ? undefined : progress.fraction}
                aria-label={PHASE[progress.phase]}
              />{" "}
              <span className="muted">{PHASE[progress.phase]}</span>
            </div>
          )}
        </form>
      )}
    </div>
  );
}
